import type { components } from "@majitap/sdk";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { ApprovalStatus, Tone } from "./labels";

type Schemas = components["schemas"];
export type Truck = Schemas["StaffTruck"];
export type Driver = Schemas["Driver"];
export type Assistant = Schemas["DriverAssistant"];
export type Fillsite = Schemas["StaffFillsite"];
export type Shift = Schemas["DriverShift"];
export type ShiftDetail = Schemas["DriverShiftDetail"];
export type ShiftAnswer = Schemas["ShiftAnswer"];
export type ShiftEndReason = NonNullable<Shift["end_reason"]>;
export type ShiftChecklist = Schemas["ShiftChecklist"];
export type ChecklistItem = Schemas["ShiftChecklistItem"];
export type CheckKind = ChecklistItem["kind"];
export type TruckStatus = Truck["status"];

/** The Fleet page's views, in the segmented control's order (`Console / Fleet` in the design). */
export const VIEWS = ["trucks", "drivers", "assistants", "fillsites", "shifts"] as const;
export type View = (typeof VIEWS)[number];
export const VIEW_LABEL: Record<View, string> = {
  trucks: "Trucks",
  drivers: "Drivers",
  assistants: "Assistants",
  fillsites: "Fill sites",
  shifts: "On shift",
};

/** Why a shift ended. */
export const END_REASON: Record<ShiftEndReason, string> = {
  driver: "Ended by the driver",
  staff: "Closed by staff",
  expired: "Closed after 24 hours",
  driver_suspended: "Driver suspended",
};

/** What a checklist item asks the driver for. */
export const CHECK_KIND: Record<CheckKind, string> = {
  photo: "Photo",
  yes_no: "Yes or no",
  number: "Number",
  text: "Text",
};

export const TRUCK_STATUS: Record<TruckStatus, [label: string, tone: Tone]> = {
  active: ["Active", "success"],
  maintenance: ["Maintenance", "warning"],
  inactive: ["Inactive", "neutral"],
};

export const PROFILE_STATUS: Record<Assistant["status"], [label: string, tone: Tone]> = {
  active: ["Active", "success"],
  inactive: ["Inactive", "neutral"],
  suspended: ["Suspended", "danger"],
};

/** The lists that aren't paginated: every row, filtered here. Dispatch reads its own copies under ["fleet", …]. */
export const listQueries = {
  assistants: queryOptions({
    queryKey: ["fleet", "assistants", "all"],
    queryFn: async () => (await ok(api.GET("/v1/admin/fleet/assistants"))).data,
  }),
  fillsites: queryOptions({
    queryKey: ["fleet", "fillsites", "all"],
    queryFn: async () => (await ok(api.GET("/v1/admin/fleet/fillsites"))).data,
  }),
  // on shift now: a handful, so one page of the most the API gives
  shifts: queryOptions({
    queryKey: ["fleet", "shifts", "open"],
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/fleet/shifts", { params: { query: { status: "open", limit: 100 } } }))).data,
  }),
};

/** A shift with its checklist. Photo links last 5 minutes, so it's fetched again after one. */
export const shiftQuery = (id: string) =>
  queryOptions({
    queryKey: ["fleet", "shift", id],
    staleTime: 60_000,
    queryFn: () => ok(api.GET("/v1/admin/fleet/shifts/{id}", { params: { path: { id } } })),
  });

/** What opening a shift for a driver asks for (P8-59); fresh each time, as trucks come and go. */
export const shiftOptionsQuery = (driverId: string) =>
  queryOptions({
    queryKey: ["fleet", "shift-options", driverId],
    staleTime: 0,
    retry: false,
    queryFn: () => ok(api.GET("/v1/admin/fleet/drivers/{id}/shift-options", { params: { path: { id: driverId } } })),
  });
export type ShiftOptions = Schemas["ShiftOptions"];
export type OpenShiftBody = Schemas["StartShiftBody"];

export const shiftChecklistQuery = queryOptions({
  queryKey: ["fleet", "shift-checklist"],
  queryFn: () => ok(api.GET("/v1/admin/fleet/shift-checklist")),
});

export const truckQuery = (id: string) =>
  queryOptions({
    queryKey: ["fleet", "truck", id],
    queryFn: () => ok(api.GET("/v1/admin/fleet/trucks/{id}", { params: { path: { id } } })),
  });

/** After a change: every fleet list, Dispatch's included. */
export function useRefreshFleet() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["fleet"] });
}

export type TruckFields = Schemas["StaffCreateTruckBody"];
export type DriverFields = Schemas["StaffCreateDriverBody"];
export type DriverDetails = Schemas["StaffUpdateDriverBody"];
export type AssistantFields = Schemas["StaffUpdateDriverAssistantBody"];
export type FillsiteFields = Schemas["StaffCreateFillsiteBody"];

const path = (id: string) => ({ params: { path: { id } } });

export const fleetActions = {
  createTruck: (body: TruckFields) => ok(api.POST("/v1/admin/fleet/trucks", { body })),
  updateTruck: (id: string, body: Schemas["StaffUpdateTruckBody"]) =>
    ok(api.PATCH("/v1/admin/fleet/trucks/{id}", { ...path(id), body })),
  retireTruck: (id: string) => ok(api.DELETE("/v1/admin/fleet/trucks/{id}", path(id))),
  createDriver: (body: DriverFields) => ok(api.POST("/v1/admin/fleet/drivers", { body })),
  updateDriver: (id: string, body: DriverDetails) =>
    ok(api.PATCH("/v1/admin/fleet/drivers/{id}", { ...path(id), body })),
  approveDriver: (id: string) => ok(api.POST("/v1/admin/fleet/drivers/{id}/approve", path(id))),
  suspendDriver: (id: string) => ok(api.POST("/v1/admin/fleet/drivers/{id}/suspend", path(id))),
  setDriverPin: (id: string, pin: string) =>
    ok(api.POST("/v1/admin/fleet/drivers/{id}/pin", { ...path(id), body: { pin } })),
  createAssistant: (body: Schemas["StaffCreateDriverAssistantBody"]) =>
    ok(api.POST("/v1/admin/fleet/assistants", { body })),
  updateAssistant: (id: string, body: AssistantFields) =>
    ok(api.PATCH("/v1/admin/fleet/assistants/{id}", { ...path(id), body })),
  removeAssistant: (id: string) => ok(api.DELETE("/v1/admin/fleet/assistants/{id}", path(id))),
  createFillsite: (body: FillsiteFields) => ok(api.POST("/v1/admin/fleet/fillsites", { body })),
  updateFillsite: (id: string, body: Schemas["StaffUpdateFillsiteBody"]) =>
    ok(api.PATCH("/v1/admin/fleet/fillsites/{id}", { ...path(id), body })),
  removeFillsite: (id: string) => ok(api.DELETE("/v1/admin/fleet/fillsites/{id}", path(id))),
  reviewFillsite: (id: string, decision: "approve" | "reject") =>
    decision === "approve"
      ? ok(api.POST("/v1/admin/fleet/fillsites/{id}/approve", path(id)))
      : ok(api.POST("/v1/admin/fleet/fillsites/{id}/reject", path(id))),
  closeShift: (id: string) => ok(api.POST("/v1/admin/fleet/shifts/{id}/close", path(id))),
  openShift: (driverId: string, body: OpenShiftBody) =>
    ok(api.POST("/v1/admin/fleet/drivers/{id}/shifts", { ...path(driverId), body })),
};

export type ChecklistItemFields = Schemas["ShiftChecklistItemBody"];

export const checklistActions = {
  requireAssistant: (requires_assistant: boolean) =>
    ok(api.PUT("/v1/admin/fleet/shift-checklist", { body: { requires_assistant } })),
  createItem: (body: ChecklistItemFields) => ok(api.POST("/v1/admin/fleet/shift-checklist/items", { body })),
  updateItem: (id: string, body: Schemas["UpdateShiftChecklistItemBody"]) =>
    ok(api.PATCH("/v1/admin/fleet/shift-checklist/items/{id}", { ...path(id), body })),
  archiveItem: (id: string) => ok(api.POST("/v1/admin/fleet/shift-checklist/items/{id}/archive", path(id))),
  reorder: (ids: string[]) => ok(api.PUT("/v1/admin/fleet/shift-checklist/items/order", { body: { ids } })),
};

/** An item moved one place up (-1) or down (+1), as the new order of ids. */
export function moved(items: readonly { id: string }[], id: string, by: -1 | 1): string[] {
  const ids = items.map((i) => i.id);
  const at = ids.indexOf(id);
  const to = at + by;
  if (at < 0 || to < 0 || to >= ids.length) return ids;
  [ids[at], ids[to]] = [ids[to], ids[at]];
  return ids;
}

/** A non-photo answer as text: "Yes", "120,345.5", the note. */
export function answerText(a: ShiftAnswer): string {
  if (a.kind === "yes_no") return a.bool_value ? "Yes" : "No";
  // number-ok: an odometer or a level, not money
  if (a.kind === "number") return a.number_value === null ? "—" : a.number_value.toLocaleString("en-KE");
  return a.text_value ?? "—";
}

/**
 * What a driver's ⋯ menu offers: approve a driver who can't sign in, suspend one who can, open a
 * shift for an approved, active one (the API refuses it if they're on shift already).
 */
export function driverActionsFor(status: ApprovalStatus, profile: Driver["status"] = "active") {
  return {
    approve: status !== "approved",
    suspend: status === "approved" || status === "pending",
    openShift: status === "approved" && profile === "active",
  };
}

/** Open shifts, longest first: the ones a driver most likely forgot to end. */
export function openShifts(shifts: Shift[]): Shift[] {
  return shifts.filter((s) => s.ended_at === null).sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at));
}

/** "7 h 20 min", "2 days": how long a shift has been open. */
export function shiftLength(startedAt: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 60_000));
  if (minutes >= 24 * 60) {
    const days = Math.floor(minutes / (24 * 60));
    return days === 1 ? "1 day" : `${days} days`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : `${h} h ${m} min`;
}

/** "3 h 10 min": how long until an open shift closes by itself; "now" once it's due. */
export function closesIn(expiresAt: string, now = Date.now()): string {
  return Date.parse(expiresAt) <= now ? "now" : shiftLength(new Date(now).toISOString(), Date.parse(expiresAt));
}

// --- the Fleet views' filters -----------------------------------------------------------

/** The Owner filter as the API's query: `owner` for own or partners, `partner_id` for one partner. */
export const ownerQuery = (owner: string | undefined): { owner?: "own" | "partners"; partner_id?: string } =>
  owner === "own" || owner === "partners" ? { owner } : owner ? { partner_id: owner } : {};

/** The Owner filter, for lists the API returns whole (assistants, fill sites). */
export const ownedBy = (owner: string | undefined) => (row: { partner_id: string | null }) =>
  !owner ||
  (owner === "own"
    ? row.partner_id === null
    : owner === "partners"
      ? row.partner_id !== null
      : row.partner_id === owner);

/** "0712 345" or "+254 712 345": the digits a phone stored as +254712345… contains. */
const phoneDigits = (q: string) => q.replace(/\D/g, "").replace(/^0/, "");

/** Whether any of the texts contains what was searched for, ignoring case; a phone by its digits. */
export function matches(q: string | undefined, texts: (string | null | undefined)[], phone?: string | null) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (texts.some((t) => t?.toLowerCase().includes(needle))) return true;
  const digits = phoneDigits(q);
  return Boolean(phone && digits.length >= 3 && phone.includes(digits));
}
